"use client";

import * as React from "react";
import { MessageSquare, Trash2 } from "lucide-react";
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../ui/sidebar";
import { ConfirmDialog } from "../admin/confirm-dialog";

/**
 * Router Link component. Each app passes its own router's Link; this package imports none.
 * Same shape as AppSidebarProps["LinkComponent"] (components/app-sidebar.tsx:45-49).
 *
 * `to`, `className` and `children` are the whole contract: everything this list needs for a row to
 * look and behave right — including the active fill and weight — travels through `className`. An
 * adapter that additionally forwards a ref and unknown props (every router `Link` does) also gets the
 * collapsed-rail tooltip, which Radix drives through a ref on the rendered element. Nothing required
 * by the story's acceptance criteria depends on that.
 */
export type ChatShellLinkComponent = React.ComponentType<{
  to: string;
  className?: string;
  children: React.ReactNode;
}>;

export interface ChatConversationItem {
  id: string;
  title: string;
  /** Rendered through LinkComponent when both are supplied; otherwise the row is a button. */
  href?: string;
  onSelect?: () => void;
  active?: boolean;
}

/**
 * Active-row styling, applied through the *declared* `className` prop. `SidebarMenuButton`'s own
 * `data-[active=true]:` variants ride on a data attribute that `asChild` merges onto the adapter, and a
 * contract-conformant adapter is not obliged to forward it — so link rows would silently lose the
 * active state. Mirrors what `components/app-sidebar.tsx` does for the same reason.
 */
const ACTIVE_ROW = "bg-sidebar-accent font-medium text-sidebar-accent-foreground";

export interface ChatConversationListProps {
  conversations: ChatConversationItem[];
  /** Omitted ⇒ no delete affordance is rendered at all. */
  onDeleteConversation?: (id: string) => void;
  LinkComponent?: ChatShellLinkComponent;
}

/**
 * The sidebar's conversation rows (or the empty state), with deletion gated behind the shared
 * ConfirmDialog. The pending row is the only state in the shell.
 */
export function ChatConversationList({
  conversations,
  onDeleteConversation,
  LinkComponent,
}: ChatConversationListProps) {
  const [pendingId, setPendingId] = React.useState<string | null>(null);

  // Below 768px the sidebar is a controlled Sheet and the chat layout is persistent, so a row
  // activation or a delete never remounts the shell to reset openMobile — the sheet would stay
  // over the transcript the user just switched to. Close it on both.
  const { isMobile, setOpenMobile } = useSidebar();
  const closeMobile = React.useCallback(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);
  const pending = conversations.find((item) => item.id === pendingId) ?? null;

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2.5 rounded-lg border border-dashed border-sidebar-border px-3 py-7 text-center group-data-[collapsible=icon]:hidden">
        <MessageSquare className="size-5.5 text-muted-foreground" />
        <span className="text-sm font-medium leading-5">No conversations yet</span>
        <span className="text-xs leading-4 text-muted-foreground">
          Ask a question to start one.
        </span>
      </div>
    );
  }

  return (
    <>
      <SidebarMenu>
        {conversations.map((item) => {
          const label = (
            <>
              <MessageSquare />
              <span className="flex-1 truncate">{item.title}</span>
            </>
          );

          return (
            <SidebarMenuItem key={item.id}>
              {/* 2px active indicator at the row's left edge — colour is not the only cue: the active
                  row also carries data-active and the variant's medium weight. */}
              {item.active ? (
                <span
                  aria-hidden="true"
                  className="absolute top-1.5 bottom-1.5 left-0 z-10 w-0.5 rounded-full bg-sidebar-primary group-data-[collapsible=icon]:hidden"
                />
              ) : null}
              {item.href && LinkComponent ? (
                // Best-effort only: asChild puts onClick on the Slot, which merges it onto the
                // adapter — but `ChatShellLinkComponent` does not declare `onClick`, so an adapter
                // implementing the published type may drop it. It still earns its place for the
                // re-tap-the-active-row case, which changes no id. The guarantee that the sheet
                // closes on a real switch is ChatSidebar's active-id effect, which needs no adapter
                // cooperation. The comment sits out here because Slot takes exactly one element child.
                <SidebarMenuButton
                  asChild
                  isActive={item.active}
                  tooltip={item.title}
                  className={item.active ? ACTIVE_ROW : undefined}
                  onClick={closeMobile}
                >
                  <LinkComponent to={item.href}>{label}</LinkComponent>
                </SidebarMenuButton>
              ) : (
                <SidebarMenuButton
                  isActive={item.active}
                  tooltip={item.title}
                  onClick={() => {
                    closeMobile();
                    item.onSelect?.();
                  }}
                >
                  {label}
                </SidebarMenuButton>
              )}
              {onDeleteConversation ? (
                <SidebarMenuAction
                  showOnHover
                  className="size-6 text-destructive"
                  aria-label={`Delete conversation: ${item.title}`}
                  onClick={() => setPendingId(item.id)}
                >
                  <Trash2 />
                </SidebarMenuAction>
              ) : null}
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
      {onDeleteConversation ? (
        <ConfirmDialog
          open={pending !== null}
          onOpenChange={(open) => {
            if (!open) setPendingId(null);
          }}
          title="Delete conversation?"
          description={`“${pending?.title ?? ""}” will be permanently deleted. This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={() => {
            if (pending) onDeleteConversation(pending.id);
            setPendingId(null);
            // Deleting the open conversation navigates to `/`; same persistent-layout reason.
            closeMobile();
          }}
        />
      ) : null}
    </>
  );
}
